<template>
  <div class="home">

    <div id = 'container'>

      <div class = 'header'>
        <img src = '@/assets/logo-square.svg' class = 'square' />
        <img src = '@/assets/logo-type.svg' class = 'type' />
      </div>


      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="loading-spinner"></div>
      </div>

      <!-- Error State -->
      <div v-else-if="error" class="error-container">
        <div class="error-icon">⚠️</div>
        <h3>Error Loading Data</h3>
        <p>{{ error }}</p>
        <button @click="loadGMVData" class="retry-button">Retry</button>
      </div>

      <!-- Dashboard Content -->
      <div v-else class="dashboard-content">
        <!-- GMV Statistics Cards -->
        <GMVStats :gmv-data="gmvData" />

        <!-- GMV Chart -->
        <div class="chart-section">
          <GMVChart :chart-data="gmvData" />
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import GMVStats from "@/components/GMVStats.vue";
import GMVChart from "@/components/GMVChart.vue";
import snowflakeService from "@/services/snowflakeService.js";
import liveGMVService from "@/services/liveGMVService.js";
import { useFavicon } from '@vueuse/core'

export default {
  name: "HomeView",
  components: {
    GMVStats,
    GMVChart,
  },
  setup() {
    // Ensure favicon is set correctly
    useFavicon('/logo-square.png');
  },
  data() {
    return {
      gmvData: [],
      loading: false,
      error: null,
      lastUpdated: null,
      backendConnected: false,
    };
  },
  async mounted() {
    await this.loadGMVData();

    // Check and debug favicon
    this.debugFavicon();

    let container = document.getElementById('container')
    let containerHeight = container.getBoundingClientRect().height
    let windowHeight = window.innerHeight

    if(window.innerWidth > 2000){
      container.style.transform = `scale(${((windowHeight-400) / containerHeight)})`
    }

  },
  beforeUnmount() {
    // Stop live GMV service when component is unmounted
    if (liveGMVService.getStatus().isRunning) {
      liveGMVService.stop();
    }
  },
  methods: {
    debugFavicon() {
      console.log('🎯 Checking favicon status...');
      const faviconLinks = document.querySelectorAll('link[rel*="icon"]');
      faviconLinks.forEach((link, index) => {
        console.log(`Favicon ${index + 1}:`, {
          rel: link.rel,
          href: link.href,
          sizes: link.sizes,
          type: link.type
        });
      });

      // Force set favicon if needed and verify PNG is loaded
      const favicon = document.getElementById('favicon');
      if (favicon) {
        console.log('✅ Found favicon element, updating href...');
        favicon.href = '/logo-square.png?v=' + Date.now();
        favicon.type = 'image/png';
        favicon.sizes = '32x32';

        // Test if the image actually loads
        const testImg = new Image();
        testImg.onload = () => console.log('✅ PNG favicon loaded successfully');
        testImg.onerror = () => console.error('❌ Failed to load PNG favicon');
        testImg.src = favicon.href;
      }
    },
    async loadGMVData() {
      this.loading = true;
      this.error = null;

      try {
        const result = await snowflakeService.getGMVData();

        if (result.success) {
          this.gmvData = result.data;
          this.lastUpdated = new Date().toLocaleString();
          this.backendConnected = true;

          // Start live GMV service after successful data load
          try {
            if (!liveGMVService.getStatus().isRunning) {
              console.log('🚀 Starting live GMV updates...');
              await liveGMVService.start();
            }
          } catch (liveError) {
            console.warn('⚠️ Failed to start live GMV service:', liveError);
            // Don't show error to user since the basic data loading worked
          }
        } else {
          // Display the actual error from backend/connection
          this.error = result.error;
          if (result.details) {
            this.error += `: ${result.details}`;
          }
          this.backendConnected = false;
        }
      } catch (err) {
        this.error = "An unexpected error occurred while loading data";
        this.backendConnected = false;
        console.error("Error loading GMV data:", err);
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.header{
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-bottom: 24px;
  margin-top: -36px;
}

.square{
  height: 48px;
  border-radius: 24px;
}

.type{
  height: 24px;
  margin-top: 2px;
}
.home {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow-y: hidden;
  box-sizing: border-box;
  padding: 40px;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
}

#container{
  width: 800px;
  padding: 24px;
  box-sizing: border-box;
  height: 660px;
  gap: 0;
  max-height: 90vh;
  background: white;
  border-radius: 18px;
  box-shadow: -20px 60px 120px rgba(#030025, 0.25);

  @media (max-width: 768px) {
    width: calc(100% - 48px);
    height: 100%;
    padding: 12px;
    box-sizing: border-box;
    border-radius: 16px;
  }
}

.header-section {
  text-align: center;
}

.page-title {
  font-size: 2.5rem;
  font-weight: 800;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  background: linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.page-subtitle {
  font-size: 1.125rem;
  color: #64748b;
  margin: 0;
  font-weight: 400;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #64748b;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-top: 3px solid #4f46e5;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
  color: #dc2626;
  text-align: center;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.retry-button {
  background: #4f46e5;
  color: white;
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  margin-top: 1rem;
  transition: background-color 0.2s;
}

.retry-button:hover {
  background: #3730a3;
}

.dashboard-content {
  max-width: 1200px;
  margin: 0 auto;
}

.chart-section {
  margin-bottom: 2rem;
}

.data-source-info {
  margin-top: 2rem;
}

.info-card {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
}

.info-card h4 {
  margin: 0 0 1rem 0;
  color: #1e293b;
  font-size: 1.125rem;
  font-weight: 600;
}

.info-card p {
  margin: 0.5rem 0;
  color: #64748b;
  font-size: 0.875rem;
}

.data-source-warning {
  margin-top: 1rem;
  padding: 1rem;
  background: #fef3c7;
  border-radius: 8px;
  border-left: 4px solid #f59e0b;
  display: flex;
  align-items: flex-start;
}

.data-source-warning ol {
  margin: 0.5rem 0 0 0;
  padding-left: 1.5rem;
}

.data-source-warning code {
  background: #f3f4f6;
  padding: 2px 4px;
  border-radius: 4px;
  font-family: "Courier New", monospace;
  font-size: 0.875rem;
}

.warning-icon {
  font-size: 1.5rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.data-source-success {
  margin-top: 1rem;
  padding: 1rem;
  background: #d1fae5;
  border-radius: 8px;
  border-left: 4px solid #15803d;
  display: flex;
  align-items: flex-start;
}

.success-icon {
  font-size: 1.5rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.data-source-warning p,
.data-source-success p {
  margin: 0.5rem 0;
  color: #374151;
}

@media (max-width: 768px) {

  #container{
    height: 560px;
    width: 200%;
    margin-bottom: 40px;
  }

  .home{
    padding: 24px;
  }


  .page-title {
    font-size: 2rem;
  }

  .page-subtitle {
    font-size: 1rem;
  }

  .chart-section{
    height: 100%;
    margin-bottom: 0;
    display: flex !important;
    flex-direction: column !important;
    justify-content: center !important;
  }

  .dashboard-content{
    flex: 1;
    //height: calc(100% - 72px);
    display: flex;
    flex-direction: column;

    :global(.chart-container){
      height: 100% !important;
      flex: 1;
    }
  }

  .header-section {
    margin-bottom: 2rem;
  }
}

@media (max-width: 400px) {
  #container{
    margin-bottom: 60px;
  }
}


</style>
